import type { GameId, OutcomeKind, PuzzleEnvelope, RunMode } from '../games/core/types';

export const RUN_SCHEMA_VERSION = 2;

export type RunPhaseName =
  'preparing' | 'splash' | 'playing' | 'outcome' | 'loading' | 'results' | 'error';

export type ScoreEvent = {
  id: string;
  gameIndex: number;
  amountMs: number;
  label: string;
  createdAt: number;
};

export type GameResult = {
  gameId: GameId;
  rawTimeMs: number;
  deltaMs: number;
  outcome: OutcomeKind;
  message: string;
  metadata: Record<string, unknown>;
};

export type RunState = {
  schemaVersion: number;
  id: string;
  seed: string;
  mode: RunMode;
  phase: RunPhaseName;
  phaseRevision: number;
  phaseStartedAt: number;
  createdAt: number;
  updatedAt: number;
  puzzles: PuzzleEnvelope[];
  currentIndex: number;
  gameStates: Record<string, unknown>;
  scoreEvents: ScoreEvent[];
  results: GameResult[];
  rawActiveMs: number;
  activeSinceMs: number | null;
  sessionElapsedMs: number;
  sessionSinceMs: number | null;
  generationProgress: { completed: number; total: number; gameName: string };
  error: string | null;
};

export type RunAction =
  | { type: 'hydrate'; state: RunState }
  | {
      type: 'create';
      id: string;
      seed: string;
      mode: RunMode;
      nowEpoch: number;
      nowPerformance: number;
    }
  | {
      type: 'generation-progress';
      completed: number;
      total: number;
      gameName: string;
      nowEpoch: number;
    }
  | { type: 'prepared'; puzzles: PuzzleEnvelope[]; nowEpoch: number; nowPerformance: number }
  | { type: 'retry-generation'; nowEpoch: number }
  | { type: 'discard' }
  | { type: 'splash-complete'; nowEpoch: number; nowPerformance: number }
  | { type: 'save-game-state'; state: unknown; nowEpoch: number }
  | {
      type: 'charge';
      id: string;
      amountMs: number;
      label: string;
      nowEpoch: number;
    }
  | {
      type: 'complete-game';
      outcome: OutcomeKind;
      baseDeltaMs: number;
      message: string;
      metadata?: Record<string, unknown>;
      nowEpoch: number;
      nowPerformance: number;
    }
  | { type: 'outcome-complete'; nowEpoch: number }
  | { type: 'loading-complete'; nowEpoch: number; nowPerformance: number }
  | { type: 'fail'; message: string; nowEpoch: number; nowPerformance: number };

export type AppSettings = {
  mode: RunMode;
  sound: boolean;
  reducedMotion: boolean;
};

export const DEFAULT_SETTINGS: AppSettings = {
  mode: 'practice',
  sound: true,
  reducedMotion: false,
};
